/**
 * motion.js — scroll reveal choreography
 *
 * The only motion module the theme needs at page level. Everything else is CSS
 * transitions on state classes.
 *
 * Principles enforced here:
 *   - Content is visible without JavaScript. The `js-motion` class that hides
 *     reveal targets is only added once this module is running and has
 *     confirmed the browser supports IntersectionObserver.
 *   - Nothing animates more than once. Re-animating on scroll-back is the
 *     single clearest tell of a template.
 *   - Elements already in the viewport on load are revealed on the next frame
 *     with their stagger intact, so the fold never sits blank waiting for a
 *     scroll event.
 *   - Reduced motion is handled in CSS at the token level; this module still
 *     runs so that `is-revealed` state stays consistent for any code reading it.
 */

const REVEAL_SELECTOR = '[data-reveal]';
const ROOT_MARGIN = '0px 0px -12% 0px';

/**
 * Failsafe window. If the observer has not reported anything by the time this
 * elapses — a suspended background tab, a browser quirk, an exception thrown
 * elsewhere on the page — everything currently on screen is revealed by
 * geometry instead. Invisible content is a far worse failure than an
 * un-animated reveal.
 */
const FAILSAFE_DELAY = 2500;

class Reveal {
  constructor() {
    this.observer = null;
    this.observed = new WeakSet();
    this.hasReported = false;
  }

  init() {
    if (!('IntersectionObserver' in window)) return;

    document.documentElement.classList.add('js-motion');

    this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
      rootMargin: ROOT_MARGIN,
      threshold: 0.01,
    });

    this.scan(document);

    // The theme editor swaps section markup in place; rescan so freshly
    // inserted content still reveals instead of staying invisible.
    document.addEventListener('shopify:section:load', (event) => this.scan(event.target));
    document.addEventListener('theme:contentchange', (event) => this.scan(event.target || document));

    window.setTimeout(() => this.failsafe(), FAILSAFE_DELAY);
  }

  /**
   * Reveals anything already within the viewport that the observer has not
   * accounted for. Runs once, and only if the observer has stayed silent.
   */
  failsafe() {
    if (this.hasReported) return;

    document.querySelectorAll(`${REVEAL_SELECTOR}:not(.is-revealed)`).forEach((element) => {
      const box = element.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) {
        element.classList.add('is-revealed');
        if (this.observer) this.observer.unobserve(element);
      }
    });
  }

  scan(root) {
    if (!root || !this.observer) return;

    const scope = root.nodeType === Node.DOCUMENT_NODE ? root : root;
    const targets = scope.querySelectorAll(REVEAL_SELECTOR);

    targets.forEach((element) => {
      if (this.observed.has(element)) return;
      this.observed.add(element);
      this.applyStagger(element);
      this.observer.observe(element);
    });
  }

  /**
   * Stagger is expressed as position-within-group, not a global index, so a
   * grid of six cards ripples across itself rather than inheriting a delay
   * from whatever happened to render above it.
   */
  applyStagger(element) {
    const group = element.closest('[data-reveal-group]');
    if (!group) return;

    const siblings = Array.from(group.querySelectorAll(REVEAL_SELECTOR));
    const index = siblings.indexOf(element);
    if (index > 0) {
      element.style.setProperty('--reveal-delay', `calc(var(--reveal-stagger) * ${index})`);
    }
  }

  onIntersect(entries) {
    this.hasReported = true;

    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      this.observer.unobserve(entry.target);
    });
  }
}

const reveal = new Reveal();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => reveal.init(), { once: true });
} else {
  reveal.init();
}

export default reveal;
